import React from 'react';
import { Typography, TypographyProps, Box, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Props for the InputLabel component
 */
export interface InputLabelProps extends Omit<TypographyProps, 'variant'> {
  /**
   * The label text
   */
  children: React.ReactNode;
  /**
   * Optional text to display after the label
   */
  optional?: boolean | string;
  /**
   * Whether the field is required
   */
  required?: boolean;
}

/**
 * Styled Typography component for input labels
 */
const StyledLabel = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: 'normal',
  letterSpacing: '-0.24px',
  marginBottom: '5px',
  fontFamily: '"Open Sans"',
  display: 'inline-block',
}));

/**
 * Styled span for optional/required indicators
 */
const OptionalSpan = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 400,
  marginLeft: '4px',
}));

const RequiredSpan = styled('span')(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: 400,
  marginLeft: '2px',
}));

/**
 * InputLabel component - A standardized label for form inputs
 * 
 * This component provides consistent styling for input labels across the application,
 * with support for optional/required indicators and theme adaptation.
 * 
 * @example
 * ```tsx
 * <InputLabel>Email Address</InputLabel>
 * <InputLabel optional>Phone Number</InputLabel>
 * <InputLabel required>Full Name</InputLabel>
 * <InputLabel optional="(Not required)">Company</InputLabel>
 * ```
 */
const InputLabel: React.FC<InputLabelProps> = ({
  children,
  optional,
  required,
  ...rest
}) => {
  const theme = useTheme();
  
  return (
    <StyledLabel
      component="div"
      sx={{ 
        color: theme.palette.text.primary,
      }}
      {...rest}
    >
      {children}
      {required && <RequiredSpan>*</RequiredSpan>}
      {optional && (
        <OptionalSpan>
          {typeof optional === 'string' ? optional : '(Optional)'}
        </OptionalSpan>
      )}
    </StyledLabel>
  );
};

export default InputLabel;